import React, { useState, useEffect } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import * as Icons from "lucide-react";
import "./ExamMode.css";
import { examData, gradeOptions } from "../../config";
import { FiX, FiClock, FiList, FiCheckCircle, FiCheck } from "react-icons/fi";
import { getMe } from "../../services/userService";
import { getCurrentTokenUsage } from "../../services/tokenUsageService";
import { useSubscription } from "../../hooks/useSubscription";

interface ExamResult {
  topics: Array<{
    topic: string;
    completed?: boolean;
    completionPercentage?: string;
    TotalEstimatedTimeDays?: number;
    subtopics: Array<{
      name: string;
      estimatedTimeDays: number;
      completed?: boolean;
    }>;
    studyTips?: string[];
  }>;
  examName?: string;
  subject?: string;
  sessionId?: string;
  _id?: string;
  createdAt?: string;
}

const getSubtopicName = (subtopic: { name?: string } | string | undefined): string => {
  if (!subtopic) {
    return "";
  }
  return typeof subtopic === "string" ? subtopic : (subtopic.name || "");
};

const isMongoObjectId = (value: unknown): value is string =>
  typeof value === "string" && /^[a-f0-9]{24}$/i.test(value.trim());

const unwrapApiPayload = (payload: any): any => {
  if (payload && typeof payload === "object" && payload.data && typeof payload.data === "object") {
    return payload.data;
  }
  return payload;
};

interface ExamTopic {
  _id: string;
  sessionId: string;
  subject: string;
  examName: string;
  topics: any[];
  createdAt: string;
}

const ExamMode: React.FC = () => {
  const { hasActiveSubscription, openSubscriptionModal } = useSubscription();
  const [isExamModeOpen, setIsExamModeOpen] = useState(false);
  const [examDays, setExamDays] = useState(30);
  const [selectedExamType, setSelectedExamType] = useState("");
  const [selectedSubject, setSelectedSubject] = useState("");
  const [selectedCountry, setSelectedCountry] = useState("UK");
  const [currentScoreType, setCurrentScoreType] = useState<"grade" | "percentage" | "score">("grade");
  const [currentScoreValue, setCurrentScoreValue] = useState("");
  const [currentScoreError, setCurrentScoreError] = useState("");
  const [expectedScoreType, setExpectedScoreType] = useState<"grade" | "percentage" | "score">("grade");
  const [expectedScoreValue, setExpectedScoreValue] = useState("");
  const [expectedScoreError, setExpectedScoreError] = useState("");

  useEffect(() => {
    const fetchCountry = async () => {
      try {
        const profile = await getMe();
        // If profile country is India, switch to India. Default is UK.
        // We can check fuzzy match or direct mapping
        const userCountry = profile.country?.trim().toLowerCase();
        if (userCountry === 'india') {
          setSelectedCountry('India');
        } else if (userCountry === 'united kingdom' || userCountry === 'uk') {
          setSelectedCountry('UK');
        }
        // Keep default UK otherwise or for other countries for now until we have data
      } catch (e) {
        console.error("Failed to fetch user country", e);
      }
    };
    fetchCountry();
  }, []);
  const [examSubmitError, setExamSubmitError] = useState("");
  const [isExamSubmitting, setIsExamSubmitting] = useState(false);
  const [examResult, setExamResult] = useState<ExamResult | null>(null);
  const [isExamResultOpen, setIsExamResultOpen] = useState(false);
  const [isFromSavedTopics, setIsFromSavedTopics] = useState(false);
  const [incompleteTopic, setIncompleteTopic] = useState<string | null>(null);
  const [incompleteSubtopic, setIncompleteSubtopic] = useState<string | null>(null);
  const [isLoadingIncomplete, setIsLoadingIncomplete] = useState(false);
  const [fetchError, setFetchError] = useState("");
  const location = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    console.log("exam mode component mounted")
  }, [])

  useEffect(() => {
    if (location.state) {
      const {
        isFromSavedTopics,
        examResult,
        incompleteTopic,
        incompleteSubtopic,
        courseId,
      } = location.state;

      if (isFromSavedTopics && examResult) {
        const normalizedCourseId =
          courseId ||
          examResult.sessionId ||
          examResult._id ||
          "";
        const normalizedExamResult = {
          ...examResult,
          sessionId: normalizedCourseId || examResult.sessionId,
          _id: examResult._id || normalizedCourseId || examResult.sessionId,
          createdAt: examResult.createdAt || new Date().toISOString(),
          examName: examResult.examName || selectedExamType || "Unknown Exam",
          subject: examResult.subject || selectedSubject || "General",
        };

        setIsFromSavedTopics(true);
        setExamResult(normalizedExamResult);
        setIncompleteTopic(incompleteTopic || null);
        setIncompleteSubtopic(incompleteSubtopic || null);
        setIsExamResultOpen(true);
        findIncompleteTopics(normalizedExamResult.subject || "", normalizedExamResult.examName || "");
      }
    }
  }, [location.state, selectedExamType, selectedSubject]);

  const toggleExamMode = () => {
    setIsExamModeOpen(!isExamModeOpen);
    if (isExamModeOpen) {
      setExamDays(30);
      setSelectedExamType("");
      setSelectedSubject("");
      setCurrentScoreType("grade");
      setCurrentScoreValue("");
      setCurrentScoreError("");
      setExpectedScoreType("grade");
      setExpectedScoreValue("");
      setExpectedScoreError("");
      setExamSubmitError("");
    }
  };

  const handleSubjectClick = (examName: string, board: string, subject: string) => {
    setSelectedExamType(`${examName} - ${board}`);
    setSelectedSubject(subject);
    setIsExamModeOpen(true);
  };

  const handleCurrentScoreChange = (val: string) => {
    setCurrentScoreValue(val);
    if (currentScoreType === "percentage") {
      if (+val < 1 || +val > 100) setCurrentScoreError("Percentage must be 1–100");
      else setCurrentScoreError("");
    } else if (currentScoreType === "score") {
      if (+val < 1 || +val > 5) setCurrentScoreError("Score must be 1–5");
      else setCurrentScoreError("");
    } else {
      setCurrentScoreError("");
    }
  };

  const handleExpectedScoreChange = (val: string) => {
    setExpectedScoreValue(val);
    if (expectedScoreType === "percentage") {
      if (+val < 1 || +val > 100) setExpectedScoreError("Percentage must be 1–100");
      else setExpectedScoreError("");
    } else if (expectedScoreType === "score") {
      if (+val < 1 || +val > 5) setExpectedScoreError("Score must be 1–5");
      else setExpectedScoreError("");
    } else {
      setExpectedScoreError("");
    }
  };

  const getCurrentSubject = () => selectedSubject || examResult?.subject || "General";
  const getCurrentExamName = () => selectedExamType || examResult?.examName || "";

  const resolveExamDocumentId = async (
    idCandidates: unknown[],
    subject: string,
    examName: string
  ): Promise<string> => {
    for (const candidate of idCandidates) {
      if (isMongoObjectId(candidate)) {
        return candidate.trim();
      }
    }

    const token = localStorage.getItem("token");
    if (!token) {
      return "";
    }

    try {
      const url = `${process.env.REACT_APP_API_URL}/api/exam-topics/mine?subject=${encodeURIComponent(subject)}&limit=20&page=1`;
      const res = await fetch(url, {
        method: "GET",
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      if (!res.ok) {
        return "";
      }

      const body = await res.json();
      const rows = Array.isArray(body) ? body : (Array.isArray(body?.data) ? body.data : []);
      const normalizedExamName = examName.trim().toLowerCase();
      const normalizedSubject = subject.trim().toLowerCase();

      const exactMatch = rows.find((row: any) =>
        isMongoObjectId(row?._id) &&
        String(row?.examName || "").trim().toLowerCase() === normalizedExamName &&
        String(row?.subject || "").trim().toLowerCase() === normalizedSubject
      );

      if (exactMatch?._id) {
        return exactMatch._id;
      }

      const fallbackMatch = rows.find((row: any) => isMongoObjectId(row?._id));
      return fallbackMatch?._id || "";
    } catch (error) {
      console.error("Failed to resolve exam document id:", error);
      return "";
    }
  };

  const findIncompleteTopics = async (subject: string, examName: string) => {
    if (!subject || !examName) return;
    setIsLoadingIncomplete(true);
    setFetchError("");
    try {
      const token = localStorage.getItem("token");
      if (!token) {
        setFetchError("Please login to continue");
        return;
      }

      const url = `${process.env.REACT_APP_API_URL}/api/exam-topics/incomplete`;
      const res = await fetch(url, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ subject, examName }),
      });

      if (!res.ok) {
        const errText = await res.text();
        throw new Error(errText || `Request failed with status ${res.status}`);
      }

      const data = await res.json();

      if (data.message === "All topics are completed") {
        setIncompleteTopic(null);
        setIncompleteSubtopic(null);
        return;
      }

      if (data.topic) {
        setIncompleteTopic(data.topic);
        setIncompleteSubtopic(data.subtopic || null);
      }
    } catch (error: any) {
      setFetchError("Failed to fetch incomplete topics");
      setIncompleteTopic(null);
      setIncompleteSubtopic(null);
    } finally {
      setIsLoadingIncomplete(false);
    }
  };

  const handleStartExamSubmit = async () => {
    if (!selectedExamType) {
      setExamSubmitError("Exam Type is required");
      return;
    }
    if (!selectedSubject) {
      setExamSubmitError("Subject is required");
      return;
    }
    if (!currentScoreValue || currentScoreError) {
      setExamSubmitError("Current Score is invalid");
      return;
    }
    if (!expectedScoreValue || expectedScoreError) {
      setExamSubmitError("Target Score is invalid");
      return;
    }

    const token = localStorage.getItem("token");
    if (!token) {
      setExamSubmitError("Please login first.");
      return;
    }

    // Check token limits for non-subscribed users
    if (!hasActiveSubscription) {
      try {
        const usage = await getCurrentTokenUsage();
        if (usage.isLimitExceeded) {
          // Credits exhausted - show subscription modal
          openSubscriptionModal();
          setIsExamSubmitting(false);
          return;
        }
      } catch (error) {
        console.error('Failed to check token usage:', error);
        // On error, allow exam generation (don't block user)
      }
    }

    setExamSubmitError("");
    setIsExamSubmitting(true);
    setFetchError("");

    try {
      const url = `${process.env.REACT_APP_API_URL}/api/exam-topics/generate`;
      const res = await fetch(url, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          examName: selectedExamType,
          subject: selectedSubject,
          timeAvailable: Number(examDays),
          expectedTarget: Number(expectedScoreType === "grade" ? currentScoreValue : expectedScoreValue),
        }),
      });

      if (!res.ok) {
        const errText = await res.text();
        throw new Error(errText || `Request failed with status ${res.status}`);
      }

      const data = await res.json();
      const payload = unwrapApiPayload(data);
      const normalizedExamName = payload?.examName || data?.examName || selectedExamType;
      const normalizedSubject = payload?.subject || data?.subject || selectedSubject;
      const normalizedCourseId = await resolveExamDocumentId(
        [
          payload?._id,
          payload?.examId,
          payload?.sessionId,
          payload?.courseId,
          data?._id,
          data?.examId,
          data?.sessionId,
          data?.courseId,
        ],
        normalizedSubject,
        normalizedExamName
      );
      const normalizedExamResult: ExamResult = {
        ...payload,
        sessionId: normalizedCourseId || payload?.sessionId || data?.sessionId,
        _id: normalizedCourseId || payload?._id || data?._id,
        createdAt: payload?.createdAt || data?.createdAt || new Date().toISOString(),
        examName: normalizedExamName,
        subject: normalizedSubject,
      };
      setExamResult(normalizedExamResult);
      setIsExamModeOpen(false);

      // Extract incomplete topics to pass in state
      let initialIncompleteTopic = "";
      let initialIncompleteSubtopic = "";
      if (normalizedExamResult.topics?.length > 0) {
        initialIncompleteTopic = normalizedExamResult.topics[0].topic || "";
        initialIncompleteSubtopic = getSubtopicName(normalizedExamResult.topics[0].subtopics?.[0]);
      }

      if (!normalizedCourseId) {
        setExamSubmitError("Exam created, but syncing failed. Please open it from My Exams.");
        return;
      }

      // Navigate to the new StudyPlanPage
      navigate(`/study-plan/${normalizedCourseId}`, {
        state: {
          topic: normalizedExamResult,
          incompleteTopic: initialIncompleteTopic,
          incompleteSubtopic: initialIncompleteSubtopic,
          courseId: normalizedCourseId
        }
      });
    } catch (error: any) {
      const message = error?.message || "Failed to generate exam topics";
      setExamSubmitError(message);
    } finally {
      setIsExamSubmitting(false);
    }
  };

  const handleResumeCourse = () => {
    setIsFromSavedTopics(true);
    if (incompleteTopic && !incompleteSubtopic) {
      findIncompleteTopics(getCurrentSubject(), getCurrentExamName());
    }
    setIsExamResultOpen(false);
    const fallbackTopic = incompleteTopic || examResult?.topics?.[0]?.topic || undefined;
    const matchedTopic =
      examResult?.topics?.find((topic) => topic.topic === fallbackTopic) || examResult?.topics?.[0];
    const fallbackSubtopic =
      incompleteSubtopic || getSubtopicName(matchedTopic?.subtopics?.[0]) || undefined;
    console.log("navigating to exam......")
    navigate("/chat", {
      state: {
        isExamMode: true,
        courseId: examResult?.sessionId || examResult?._id || location.state?.courseId || "",
        subject: getCurrentSubject(),
        examName: getCurrentExamName(),
        incompleteTopic: fallbackTopic,
        incompleteSubtopic: fallbackSubtopic,
      },
    }
    );
  };

  const renderTopicsTree = (highlightIncomplete: boolean = false) => {
    const topics = examResult?.topics || [];
    if (!Array.isArray(topics) || topics.length === 0) {
      return (
        <p style={{ opacity: 0.8, color: "var(--text-muted)", textAlign: "center", margin: "20px 0", fontSize: 16 }}>
          No topics found.
        </p>
      );
    }

    return (
      <div className="topics-tree" style={{ display: "grid", rowGap: 20, width: "100%" }}>
        {topics.map((t: any, idx: number) => {
          const key = `${t.topic}-${idx}`;
          const tips = Array.isArray(t.studyTips) ? t.studyTips : [];
          const topicTotalDays = t.TotalEstimatedTimeDays || t.ToalEstimatedTimeDays || 0;
          const subtopics = Array.isArray(t.subtopics) ? t.subtopics : [];
          const topicCompleted = !!t.completed;
          const topicCompletionPercentage = t.completionPercentage ? parseInt(t.completionPercentage) : 0;
          const isIncompleteHighlight = highlightIncomplete && !topicCompleted && t.topic === incompleteTopic;

          return (
            <div
              key={key}
              className="topic-item"
              style={{
                background: "var(--surface-0)",
                borderRadius: 12,
                marginBottom: 16,
                padding: 0,
                boxShadow: "0 2px 8px rgba(0,0,0,0.1), 0 1px 3px rgba(0,0,0,0.06)",
                transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
                overflow: "hidden",
                position: "relative",
                opacity: 0,
                transform: "translateY(10px)",
                animation: `slideIn 0.4s ease-out ${idx * 0.1}s forwards`,
                border: isIncompleteHighlight ? "3px solid var(--brand-blue)" : "1px solid transparent",
              }}
            >
              <div
                className="topic-header"
                style={{
                  padding: "20px 24px 16px",
                  background: isIncompleteHighlight
                    ? "linear-gradient(135deg, rgba(96, 165, 250, 0.16) 0%, rgba(96, 165, 250, 0.26) 100%)"
                    : "linear-gradient(135deg, var(--surface-0) 0%, var(--surface-1) 100%)",
                  display: "flex",
                  alignItems: "center",
                  gap: 16,
                  borderBottom: "2px solid var(--border-2)",
                  color: "var(--text-1)",
                  fontSize: 18,
                  fontWeight: 700,
                  position: "relative",
                }}
              >
                {isIncompleteHighlight && (
                  <div
                    style={{
                      position: "absolute",
                      top: 8,
                      right: 8,
                      background: "var(--brand-blue)",
                      color: "white",
                      fontSize: 10,
                      padding: "2px 6px",
                      borderRadius: 8,
                      fontWeight: 600,
                    }}
                  >
                    NEXT
                  </div>
                )}
                <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
                  <label
                    style={{
                      display: "inline-flex",
                      alignItems: "center",
                      justifyContent: "center",
                      width: 24,
                      height: 24,
                      borderRadius: "50%",
                      background: topicCompleted
                        ? "linear-gradient(135deg, #10b981, #059669)"
                        : "var(--surface-0)",
                      border: topicCompleted ? "none" : "2px solid var(--border-strong)",
                      cursor: "default",
                      flexShrink: 0,
                      position: "relative",
                    }}
                  >
                    <input
                      type="checkbox"
                      checked={topicCompleted}
                      readOnly={true}
                      style={{
                        position: "absolute",
                        width: "100%",
                        height: "100%",
                        margin: 0,
                        opacity: 0,
                        cursor: "default",
                      }}
                    />
                    {topicCompleted && (
                      <FiCheck
                        style={{
                          color: "white",
                          fontSize: 16,
                          pointerEvents: "none",
                          userSelect: "none",
                          zIndex: 1,
                        }}
                      />
                    )}
                  </label>
                  <span style={{ fontSize: 20, color: "var(--text-2)" }}>{t.topic || `Topic ${idx + 1}`}</span>
                </div>
                <span
                  style={{
                    marginLeft: "auto",
                    display: "inline-flex",
                    alignItems: "center",
                    gap: 8,
                    color: "var(--text-3)",
                    fontSize: 16,
                    fontWeight: 500,
                    background: "var(--surface-2)",
                    padding: "6px 12px",
                    borderRadius: 20,
                  }}
                >
                  <FiClock style={{ fontSize: 16 }} />
                  {topicTotalDays ? `${topicTotalDays}d` : "—"}
                </span>
              </div>

              <div style={{ padding: "0 24px", marginBottom: 12 }}>
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    marginBottom: 4,
                  }}
                >
                  <span style={{ fontSize: 14, color: "var(--text-3)" }}>Progress</span>
                  <span style={{ fontSize: 14, color: "var(--text-3)", fontWeight: 500 }}>
                    {topicCompletionPercentage}%
                  </span>
                </div>
                <div
                  style={{
                    height: 8,
                    background: "var(--surface-2)",
                    borderRadius: 4,
                    overflow: "hidden",
                  }}
                >
                  <div
                    style={{
                      height: "100%",
                      width: `${topicCompletionPercentage}%`,
                      background:
                        topicCompletionPercentage > 70
                          ? "linear-gradient(90deg, #10b981, #059669)"
                          : topicCompletionPercentage > 30
                            ? "linear-gradient(90deg, #f59e0b, #d97706)"
                            : "linear-gradient(90deg, #ef4444, #dc2626)",
                      borderRadius: 4,
                      transition: "width 0.3s ease",
                    }}
                  />
                </div>
              </div>

              <div
                className="topic-body"
                style={{
                  padding: "0 24px 24px",
                  display: "grid",
                  rowGap: 24,
                  background: "linear-gradient(180deg, var(--surface-0) 0%, var(--surface-1) 100%)",
                }}
              >
                <div className="subtopics" style={{ display: "grid", rowGap: 16 }}>
                  <div
                    style={{
                      fontWeight: 700,
                      display: "inline-flex",
                      alignItems: "center",
                      gap: 12,
                      color: "var(--text-2)",
                      fontSize: 17,
                      marginBottom: 8,
                    }}
                  >
                    <div
                      style={{
                        background: "linear-gradient(135deg, #3b82f6, #1d4ed8)",
                        borderRadius: "50%",
                        padding: 8,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                      }}
                    >
                      <FiList style={{ fontSize: 16, color: "white" }} />
                    </div>
                    Subtopics
                  </div>
                  {subtopics.length > 0 ? (
                    <div style={{ display: "grid", rowGap: 10 }}>
                      {subtopics.map((subtopic: any, sIdx: number) => {
                        const subtopicCompleted = subtopic.completed || false;
                        const isSubtopicHighlight = highlightIncomplete && subtopic.name === incompleteSubtopic;

                        return (
                          <div
                            key={`${key}-sub-${sIdx}`}
                            style={{
                              background: isSubtopicHighlight ? "rgba(245, 158, 11, 0.14)" : "var(--surface-0)",
                              borderRadius: 10,
                              padding: "16px 20px",
                              margin: 0,
                              color: "var(--text-2)",
                              lineHeight: 1.6,
                              fontSize: 15,
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "space-between",
                              boxShadow: "0 1px 3px rgba(0,0,0,0.1)",
                              border: isSubtopicHighlight ? "2px solid #f59e0b" : "1px solid var(--border-2)",
                              transition: "all 0.2s ease",
                              cursor: "default",
                              transform: "translateX(0)",
                              animation: `slideInLeft 0.3s ease-out ${(sIdx * 0.05) + 0.2}s forwards`,
                            }}
                            onMouseEnter={(e) => {
                              e.currentTarget.style.transform = "translateX(4px)";
                              e.currentTarget.style.boxShadow = "0 4px 12px rgba(0,0,0,0.15)";
                            }}
                            onMouseLeave={(e) => {
                              e.currentTarget.style.transform = "translateX(0)";
                              e.currentTarget.style.boxShadow = "0 1px 3px rgba(0,0,0,0.1)";
                            }}
                          >
                            <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
                              <label
                                style={{
                                  display: "inline-flex",
                                  alignItems: "center",
                                  justifyContent: "center",
                                  width: 20,
                                  height: 20,
                                  borderRadius: "50%",
                                  background: subtopicCompleted
                                    ? "linear-gradient(135deg, #10b981, #059669)"
                                    : "var(--surface-0)",
                                  border: subtopicCompleted ? "none" : "2px solid var(--border-strong)",
                                  cursor: "default",
                                  flexShrink: 0,
                                  position: "relative",
                                }}
                              >
                                <input
                                  type="checkbox"
                                  checked={subtopicCompleted}
                                  readOnly={true}
                                  style={{
                                    position: "absolute",
                                    width: "100%",
                                    height: "100%",
                                    margin: 0,
                                    opacity: 0,
                                    cursor: "default",
                                  }}
                                />
                                {subtopicCompleted && (
                                  <FiCheck
                                    style={{
                                      color: "white",
                                      fontSize: 12,
                                      pointerEvents: "none",
                                      userSelect: "none",
                                      zIndex: 1,
                                    }}
                                  />
                                )}
                              </label>
                              <span style={{ fontWeight: 500 }}>{subtopic.name}</span>
                              {isSubtopicHighlight && (
                                <span
                                  style={{
                                    background: "#f59e0b",
                                    color: "white",
                                    fontSize: 10,
                                    padding: "2px 6px",
                                    borderRadius: 8,
                                    fontWeight: 600,
                                  }}
                                >
                                  NEXT
                                </span>
                              )}
                            </div>
                            <span
                              style={{
                                display: "inline-flex",
                                alignItems: "center",
                                gap: 6,
                                color: "var(--text-3)",
                                fontSize: 13,
                                background: "var(--surface-1)",
                                padding: "4px 8px",
                                borderRadius: 12,
                                fontWeight: 500,
                              }}
                            >
                              <FiClock style={{ fontSize: 12 }} />
                              {subtopic.estimatedTimeDays}d
                            </span>
                          </div>
                        );
                      })}
                    </div>
                  ) : (
                    <p style={{ color: "var(--text-muted)", fontStyle: "italic", margin: 0, fontSize: 15 }}>
                      No subtopics available.
                    </p>
                  )}
                </div>

                {tips.length > 0 && (
                  <div className="tips" style={{ display: "grid", rowGap: 16 }}>
                    <div
                      style={{
                        fontWeight: 700,
                        display: "inline-flex",
                        alignItems: "center",
                        gap: 12,
                        color: "var(--text-2)",
                        fontSize: 17,
                        marginBottom: 8,
                      }}
                    >
                      <div
                        style={{
                          background: "linear-gradient(135deg, #10b981, #059669)",
                          borderRadius: "50%",
                          padding: 8,
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                        }}
                      >
                        <FiCheckCircle style={{ fontSize: 16, color: "white" }} />
                      </div>
                      Study Tips
                    </div>
                    <div style={{ display: "grid", rowGap: 10 }}>
                      {tips.map((tip: string, tIdx: number) => (
                        <div
                          key={`${key}-tip-${tIdx}`}
                          style={{
                            background: "var(--surface-0)",
                            borderRadius: 10,
                            padding: "14px 18px",
                            margin: 0,
                            color: "var(--text-2)",
                            fontSize: 14,
                            lineHeight: 1.5,
                            boxShadow: "0 1px 3px rgba(0,0,0,0.1)",
                            border: "1px solid var(--border-2)",
                            transition: "all 0.2s ease",
                            cursor: "default",
                            display: "flex",
                            alignItems: "flex-start",
                            gap: 10,
                            animation: `slideInRight 0.3s ease-out ${(tIdx * 0.05) + 0.3}s forwards`,
                          }}
                          onMouseEnter={(e) => {
                            e.currentTarget.style.transform = "translateY(-2px)";
                            e.currentTarget.style.boxShadow = "0 4px 12px rgba(0,0,0,0.15)";
                          }}
                          onMouseLeave={(e) => {
                            e.currentTarget.style.transform = "translateY(0)";
                            e.currentTarget.style.boxShadow = "0 1px 3px rgba(0,0,0,0.1)";
                          }}
                        >
                          <div
                            style={{
                              width: 6,
                              height: 6,
                              borderRadius: "50%",
                              background: "linear-gradient(135deg, #f59e0b, #d97706)",
                              flexShrink: 0,
                              marginTop: 6,
                            }}
                          />
                          <span style={{ fontWeight: 400 }}>{tip}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </div>
          );
        })}
        <div style={{ display: "flex", justifyContent: "center", alignItems: "center", height: "100px" }}>
          <button
            className="resume-course"
            disabled={!examResult || (!incompleteTopic && !incompleteSubtopic)}
            onClick={handleResumeCourse}
            style={{
              background: !examResult || (!incompleteTopic && !incompleteSubtopic)
                ? "var(--border-strong)"
                : "var(--brand-blue)",
              color: "white",
              border: "none",
              borderRadius: 8,
              padding: "12px 24px",
              fontSize: 16,
              fontWeight: 500,
              cursor: !examResult || (!incompleteTopic && !incompleteSubtopic)
                ? "not-allowed"
                : "pointer",
            }}
          >
            Resume Course
          </button>
        </div>
      </div>
    );
  };

  return (
    <div className="exam-mode-container">
      <div className="exam-country-selector">
        <label htmlFor="country-select">Select Country:</label>
        <select
          id="country-select"
          value={selectedCountry}
          onChange={(e) => setSelectedCountry(e.target.value)}
          className="country-dropdown"
        >
          <option value="UK">United Kingdom</option>
          <option value="India">India</option>
        </select>
      </div>

      {examData
        .filter((cat) => (selectedCountry === 'India' ? cat.country === 'India' : (cat.country === 'UK' || !cat.country)))
        .map((category) => (
          <div key={category.name} className="exam-category">
            <h2>{category.name}</h2>
            {category.boards.map((board) => (
              <div key={board.board} className="exam-board">
                <h3>{board.board}</h3>
                <div className="subjects">
                  {board.subjects.map((subject) => {
                    const Icon = (Icons as any)[subject.icon] || Icons.Circle;
                    return (
                      <div
                        key={subject.name}
                        className="subject"
                        onClick={() => handleSubjectClick(category.name, board.board, subject.name)}
                      >
                        <Icon />
                        {subject.name}
                      </div>
                    );
                  })}
                </div>
              </div>
            ))}
          </div>
        ))}

      {isExamModeOpen && (
        <div className="exam-mode-overlay" onClick={toggleExamMode}>
          <div className="exam-mode-dialog" onClick={(e) => e.stopPropagation()}>
            <div className="exam-mode-header">
              <h2>Exam Mode</h2>
              <button className="exam-mode-close" onClick={toggleExamMode} aria-label="Close exam mode">
                <FiX size={24} />
              </button>
            </div>

            <div className="exam-mode-content">
              <div className="exam-mode-section">
                <h3>Number of Days</h3>
                <div className="exam-days">
                  <input
                    type="number"
                    min="30"
                    max="365"
                    value={examDays}
                    onChange={(e) => setExamDays(parseInt(e.target.value))}
                    className="exam-days-input"
                    placeholder="Enter number of days"
                  />
                  <span className="exam-days-hint">(30-365 days)</span>
                </div>
              </div>

              <div className="exam-mode-section">
                <h3>Exam Type</h3>
                <input type="text" value={selectedExamType} readOnly className="exam-type-select" />
              </div>

              <div className="exam-mode-section">
                <h3>Subject</h3>
                <input type="text" value={selectedSubject} readOnly className="exam-type-select" />
              </div>

              <div className="exam-mode-section">
                <h3>Current Score</h3>
                <div className="score-type-selector">
                  <label>
                    <input
                      type="radio"
                      value="grade"
                      checked={currentScoreType === "grade"}
                      onChange={(e) => setCurrentScoreType(e.target.value as any)}
                    />
                    Grade
                  </label>
                  <label>
                    <input
                      type="radio"
                      value="percentage"
                      checked={currentScoreType === "percentage"}
                      onChange={(e) => setCurrentScoreType(e.target.value as any)}
                    />
                    Percentage
                  </label>
                  <label>
                    <input
                      type="radio"
                      value="score"
                      checked={currentScoreType === "score"}
                      onChange={(e) => setCurrentScoreType(e.target.value as any)}
                    />
                    Score
                  </label>
                </div>

                <div className="score-input-container">
                  {currentScoreType === "grade" && (
                    <select
                      value={currentScoreValue}
                      onChange={(e) => setCurrentScoreValue(e.target.value)}
                      className="exam-grade-select"
                    >
                      <option value="">Select Grade</option>
                      {gradeOptions.map((grade) => (
                        <option key={grade} value={grade}>
                          {grade}
                        </option>
                      ))}
                    </select>
                  )}

                  {currentScoreType === "percentage" && (
                    <input
                      type="number"
                      min="1"
                      max="100"
                      value={currentScoreValue}
                      onChange={(e) => handleCurrentScoreChange(e.target.value)}
                      placeholder="Enter percentage (1-100)"
                      className="score-input"
                    />
                  )}

                  {currentScoreType === "score" && (
                    <input
                      type="number"
                      min="1"
                      max="5"
                      step="0.1"
                      value={currentScoreValue}
                      onChange={(e) => handleCurrentScoreChange(e.target.value)}
                      placeholder="Enter score (1-5)"
                      className="score-input"
                    />
                  )}

                  {currentScoreError && <div className="score-error">{currentScoreError}</div>}
                </div>
              </div>

              <div className="exam-mode-section">
                <h3>Target Score</h3>
                <div className="score-type-selector">
                  <label>
                    <input
                      type="radio"
                      value="grade"
                      checked={expectedScoreType === "grade"}
                      onChange={(e) => setExpectedScoreType(e.target.value as any)}
                    />
                    Grade
                  </label>
                  <label>
                    <input
                      type="radio"
                      value="percentage"
                      checked={expectedScoreType === "percentage"}
                      onChange={(e) => setExpectedScoreType(e.target.value as any)}
                    />
                    Percentage
                  </label>
                  <label>
                    <input
                      type="radio"
                      value="score"
                      checked={expectedScoreType === "score"}
                      onChange={(e) => setExpectedScoreType(e.target.value as any)}
                    />
                    Score
                  </label>
                </div>

                <div className="score-input-container">
                  {expectedScoreType === "grade" && (
                    <select
                      value={expectedScoreValue}
                      onChange={(e) => setExpectedScoreValue(e.target.value)}
                      className="exam-grade-select"
                    >
                      <option value="">Select Grade</option>
                      {gradeOptions.map((grade) => (
                        <option key={grade} value={grade}>
                          {grade}
                        </option>
                      ))}
                    </select>
                  )}

                  {expectedScoreType === "percentage" && (
                    <input
                      type="number"
                      min="1"
                      max="100"
                      value={expectedScoreValue}
                      onChange={(e) => handleExpectedScoreChange(e.target.value)}
                      placeholder="Enter percentage (1-100)"
                      className="score-input"
                    />
                  )}

                  {expectedScoreType === "score" && (
                    <input
                      type="number"
                      min="1"
                      max="5"
                      step="0.1"
                      value={expectedScoreValue}
                      onChange={(e) => handleExpectedScoreChange(e.target.value)}
                      placeholder="Enter score (1-5)"
                      className="score-input"
                    />
                  )}

                  {expectedScoreError && <div className="score-error">{expectedScoreError}</div>}
                </div>
              </div>

              <div className="exam-mode-actions">
                <button
                  className="exam-start-btn"
                  onClick={handleStartExamSubmit}
                  disabled={isExamSubmitting || !!currentScoreError || !!expectedScoreError}
                >
                  {isExamSubmitting ? "Generating…" : "Generate Exam"}
                </button>
                <button className="exam-cancel-btn" onClick={toggleExamMode}>
                  Cancel
                </button>
              </div>

              {examSubmitError && (
                <div className="sessions-error" style={{ marginTop: 8 }}>
                  <span className="error-text">{examSubmitError}</span>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

    </div>
  );
};

export default ExamMode;
